import { z } from "zod";
import { IMAGE_FOCUSES, IMAGE_LOOK_KINDS, SOCIAL_PLATFORMS } from "./types";
import { canonicalLanguage, isValidTimeZone } from "./locale";
import { FONT_FAMILY, FONT_HREF } from "./typePairings";
/** Posts keep only a small revision pointer here; their bodies live in Postgres. */
export const MAX_RECORDS = 2048;
export const MAX_COLLECTIONS = 256;
export const MAX_COLLECTION_MEMBERS = 64;
export const MAX_TEXT_LENGTH = 8_000;
/** Search engines cut titles at about 60 characters and descriptions at about 160. */
export const MAX_SITE_TITLE_LENGTH = 70;
export const MAX_SITE_DESCRIPTION_LENGTH = 300;
export const MAX_PAGE_OVERRIDES = 32;
export const PAGE_PATH = /^\/[^\s?#]{0,200}$/;
export const MAX_DOCUMENT_CHARS = 2_500_000;
export const MAX_DOCUMENT_VALUES = 10_000;
const identifier = z.string().regex(/^[a-z][a-z0-9_-]{0,63}$/i, "Invalid identifier");
const text = z.string().min(1).max(MAX_TEXT_LENGTH);
const hexDigest = z.string().regex(/^[a-f0-9]{64}$/i, "Expected SHA-256 hex digest");
const hexColor = z.string().regex(/^#[a-fA-F0-9]{6}$/, "Expected hex color");
const designerOrigin = z.literal("designer").optional();
function fail(message) { throw new Error(message); }
function ensureUnique(values, description) {
    if (new Set(values).size !== values.length)
        fail(`Duplicate ${description}`);
}
function preflightDocument(input) {
    const seen = new WeakSet();
    const stack = [input];
    let estimatedChars = 0;
    let values = 0;
    while (stack.length > 0) {
        const value = stack.pop();
        values += 1;
        if (values > MAX_DOCUMENT_VALUES)
            fail("Document has too many values");
        if (typeof value === "string")
            estimatedChars += value.length + 2;
        else if (typeof value === "number")
            estimatedChars += 24;
        else if (typeof value === "boolean")
            estimatedChars += 5;
        else if (value === null)
            estimatedChars += 4;
        else if (typeof value === "object") {
            if (seen.has(value))
                fail("Document must not contain cyclic references");
            seen.add(value);
            for (const [key, child] of (Array.isArray(value) ? value.entries() : Object.entries(value))) {
                estimatedChars += String(key).length + 3;
                stack.push(child);
            }
        }
        else
            estimatedChars += 16;
        if (estimatedChars > MAX_DOCUMENT_CHARS)
            fail("Document exceeds size limit");
    }
}
function unsafeUrlSyntax(value) {
    const control = Array.from(value).some((character) => character.charCodeAt(0) <= 31 || character.charCodeAt(0) === 127);
    return value.includes("\\") || /%(?:2e|2f|5c|0[0-9a-f]|1[0-9a-f]|7f)/i.test(value) || control;
}
const internalPath = /^\/(?:[a-z0-9]+(?:-[a-z0-9]+)*)?(?:\/[a-z0-9]+(?:-[a-z0-9]+)*)*$/;
const slug = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const safeUrl = z.string().max(2_048).superRefine((value, context) => {
    if (unsafeUrlSyntax(value)) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "URL contains unsafe syntax" });
        return;
    }
    if (internalPath.test(value) || /^#[a-z][a-z0-9_-]{0,63}$/i.test(value))
        return;
    try {
        const url = new URL(value);
        if (!["https:", "http:", "mailto:", "tel:"].includes(url.protocol))
            context.addIssue({ code: z.ZodIssueCode.custom, message: "URL protocol is not allowed" });
        if (url.username || url.password)
            context.addIssue({ code: z.ZodIssueCode.custom, message: "URL credentials are not allowed" });
        if ((url.protocol === "mailto:" || url.protocol === "tel:") && url.hostname)
            context.addIssue({ code: z.ZodIssueCode.custom, message: "URL is malformed" });
    }
    catch {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "URL is invalid" });
    }
});
/** Checks one owner-entered link the way records do: https, http, mailto, tel, a site path or an anchor. */
export function isSafeUrl(value) { return safeUrl.safeParse(value).success; }
const safeObjectKey = z.string().regex(/^assets\/[a-zA-Z0-9][a-zA-Z0-9._/-]{0,191}$/, "Invalid asset object key")
    .refine((value) => !value.includes("..") && !value.includes("//") && !value.includes("\\"), "Invalid asset object key");
const AssetRefSchema = z.object({
    assetId: identifier,
    version: z.number().int().positive().max(1_000_000),
    objectKey: safeObjectKey,
    mimeType: z.enum(["image/svg+xml", "image/png", "image/jpeg", "image/webp"]),
    sha256: hexDigest,
}).strict();
export const ImageLookSchema = z.object({
    description: z.string().min(1).max(160),
    kind: z.enum(IMAGE_LOOK_KINDS),
    colours: z.array(z.string().regex(/^#[a-f0-9]{6}$/, "Expected lowercase hex colour")).max(5),
    focus: z.enum(IMAGE_FOCUSES),
    notes: z.string().max(300),
}).strict();
const SOCIAL_HOSTS = {
    instagram: ["instagram.com", "www.instagram.com"], tiktok: ["tiktok.com", "www.tiktok.com"],
    facebook: ["facebook.com", "www.facebook.com", "fb.com"], x: ["x.com", "www.x.com", "twitter.com", "www.twitter.com"],
    linkedin: ["linkedin.com", ".linkedin.com"], youtube: ["youtube.com", "www.youtube.com", "youtu.be"],
    threads: ["threads.net", "www.threads.net"], telegram: ["t.me", "telegram.me"], whatsapp: ["wa.me", "whatsapp.com", "www.whatsapp.com"],
    github: ["github.com", "www.github.com"], gitlab: ["gitlab.com", "www.gitlab.com"], behance: ["behance.net", "www.behance.net"],
    dribbble: ["dribbble.com", "www.dribbble.com"], pinterest: ["pinterest.com", "www.pinterest.com", "sg.pinterest.com", "pin.it"],
    medium: ["medium.com", ".medium.com"], substack: ["substack.com", ".substack.com"], spotify: ["open.spotify.com"],
    soundcloud: ["soundcloud.com", "www.soundcloud.com", "on.soundcloud.com"], bluesky: ["bsky.app"], snapchat: ["snapchat.com", "www.snapchat.com"],
    discord: ["discord.gg", "discord.com", "www.discord.com"], twitch: ["twitch.tv", "www.twitch.tv"], vimeo: ["vimeo.com", "www.vimeo.com"],
    xiaohongshu: ["xiaohongshu.com", "www.xiaohongshu.com", "xhslink.com"], website: [],
};
const socialUrl = z.string().max(2_048).superRefine((value, context) => {
    let url;
    try {
        url = new URL(value);
    }
    catch {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "Social URL is invalid" });
        return;
    }
    if (url.protocol !== "https:" || url.username || url.password)
        context.addIssue({ code: z.ZodIssueCode.custom, message: "Social URL must be a plain https link" });
});
const contact = z.object({
    label: text,
    whatsapp: z.string().regex(/^[1-9]\d{7,14}$/).optional(),
    message: text.optional(),
    phone: z.string().regex(/^\+[1-9]\d{7,14}$/).optional(),
    email: z.string().trim().email().max(320).optional(),
}).strict().refine((fields) => Boolean(fields.whatsapp || fields.phone || fields.email), "A contact needs at least one channel");
/** An item's optional photo (a media record id, checked in `validateContent`) and link. */
const itemExtras = { image: identifier.optional(), url: safeUrl.optional() };
export const PostFieldsSchema = z.object({
    title: z.string().trim().min(1).max(160), slug: z.string().regex(slug).max(120), status: z.enum(["draft", "published"]),
    publishAt: z.string().datetime({ offset: true }).optional(), summary: z.string().max(500).optional(),
    cover: z.object({ assetId: identifier, version: z.number().int().positive(), alt: z.string().max(500) }).strict().optional(), tags: z.array(identifier).max(16), revision: hexDigest,
}).strict();
const ContentRecordSchema = z.discriminatedUnion("kind", [
    z.object({ id: identifier, kind: z.literal("profile"), fields: z.object({ name: text, bio: text }).strict(), origin: designerOrigin }).strict(),
    z.object({ id: identifier, kind: z.literal("copy"), fields: z.object({ title: text, body: text }).strict(), origin: designerOrigin, slot: z.literal(true).optional() }).strict(),
    z.object({ id: identifier, kind: z.literal("service"), fields: z.object({ title: text, description: text, ...itemExtras }).strict(), origin: designerOrigin }).strict(),
    z.object({ id: identifier, kind: z.literal("project"), fields: z.object({ title: text, description: text, ...itemExtras }).strict(), origin: designerOrigin }).strict(),
    z.object({ id: identifier, kind: z.literal("faq"), fields: z.object({ question: text, answer: text }).strict(), origin: designerOrigin }).strict(),
    z.object({ id: identifier, kind: z.literal("link"), fields: z.object({ label: text, url: safeUrl }).strict(), origin: designerOrigin }).strict(),
    z.object({ id: identifier, kind: z.literal("media"), fields: z.object({ alt: text, asset: AssetRefSchema, original: AssetRefSchema.optional(), crop: z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1), width: z.number().positive().max(1), height: z.number().positive().max(1) }).strict().refine((crop) => crop.x + crop.width <= 1 && crop.y + crop.height <= 1, "Crop must fit inside the image").optional(), preview: AssetRefSchema.optional(), look: ImageLookSchema.optional() }).strict().refine((fields) => Boolean(fields.crop) === Boolean(fields.original), "A crop needs its original"), origin: designerOrigin }).strict(),
    z.object({ id: identifier, kind: z.literal("contact"), fields: contact, origin: designerOrigin }).strict(),
    z.object({ id: identifier, kind: z.literal("social"), fields: z.object({ platform: z.enum(SOCIAL_PLATFORMS), url: socialUrl }).strict().superRefine((fields, context) => {
            const allowed = SOCIAL_HOSTS[fields.platform];
            if (allowed.length === 0)
                return;
            let hostname = "";
            try {
                hostname = new URL(fields.url).hostname.toLowerCase();
            }
            catch {
                return;
            }
            if (!allowed.some((host) => host.startsWith(".") ? hostname.endsWith(host) && hostname.length > host.length : hostname === host))
                context.addIssue({ code: z.ZodIssueCode.custom, message: `${fields.platform} URL is invalid` });
        }), origin: designerOrigin }).strict(),
    z.object({ id: identifier, kind: z.literal("item"), fields: z.object({ title: z.string().min(1).max(120), text: z.string().min(1).max(600), detail: z.string().min(1).max(200).optional(), ...itemExtras }).strict(), origin: designerOrigin, slot: z.literal(true).optional() }).strict(),
    z.object({ id: identifier, kind: z.literal("post"), fields: PostFieldsSchema }).strict(),
    z.object({ id: identifier, kind: z.literal("brand"), fields: z.object({
            accent: hexColor,
            typeface: z.enum(["editorial", "classic", "literary", "modern", "geometric", "friendly", "statement"]).optional(),
            fonts: z.object({ display: z.string().regex(FONT_FAMILY), body: z.string().regex(FONT_FAMILY), href: z.string().regex(FONT_HREF) }).strict().optional(),
            palette: z.array(hexColor).max(4).optional(),
            voice: z.object({ tone: z.string().trim().min(1).max(200), audience: z.string().trim().min(1).max(400).optional(), write: z.string().trim().min(1).max(400).optional(), avoid: z.string().trim().min(1).max(400).optional() }).strict().optional(),
            setBy: z.object({ accent: z.enum(["owner", "designer"]).optional(), typeface: z.enum(["owner", "designer"]).optional(), fonts: z.enum(["owner", "designer"]).optional(), palette: z.enum(["owner", "designer"]).optional(), voice: z.enum(["owner", "designer"]).optional() }).strict().optional(),
        }).strict() }).strict(),
    z.object({ id: identifier, kind: z.literal("site"), fields: z.object({
            title: z.string().trim().min(1).max(MAX_SITE_TITLE_LENGTH).optional(),
            description: z.string().trim().min(1).max(MAX_SITE_DESCRIPTION_LENGTH).optional(),
            icon: AssetRefSchema.optional(),
            image: AssetRefSchema.optional(),
            analytics: z.object({ googleTagId: z.string().regex(/^(G|GT|AW)-[A-Z0-9]{4,20}$/) }).strict().optional(),
            language: z.string().max(35).refine((value) => canonicalLanguage(value) === value, "Expected a language tag like en-SG").optional(),
            timeZone: z.string().max(64).refine(isValidTimeZone, "Expected an IANA time zone like Asia/Singapore").optional(),
            pages: z.record(z.object({
                title: z.string().trim().min(1).max(MAX_SITE_TITLE_LENGTH).optional(),
                description: z.string().trim().min(1).max(MAX_SITE_DESCRIPTION_LENGTH).optional(),
            }).strict().refine((page) => Boolean(page.title || page.description), "A page override needs a title or description"))
                .refine((pages) => Object.keys(pages).length <= MAX_PAGE_OVERRIDES && Object.keys(pages).every((path) => PAGE_PATH.test(path)), "Page overrides are invalid").optional(),
        }).strict() }).strict(),
]);
const ContentSchema = z.object({
    schemaVersion: z.literal(1), siteId: identifier, id: identifier,
    recordsById: z.record(ContentRecordSchema).refine((records) => Object.keys(records).length <= MAX_RECORDS, "Too many records"),
    collections: z.record(z.union([
        z.object({ memberIds: z.array(identifier).max(MAX_COLLECTION_MEMBERS), designOrder: z.array(identifier).max(MAX_COLLECTION_MEMBERS).optional() }).strict(),
        z.object({ rule: z.object({ kind: identifier, tag: identifier.optional(), sort: z.enum(["newest", "oldest", "title"]) }).strict(), group: z.object({ label: z.string().trim().min(1).max(80), slug: z.string().regex(slug).max(80), parentId: identifier.optional() }).strict().optional() }).strict(),
    ])).refine((collections) => Object.keys(collections).length <= MAX_COLLECTIONS, "Too many collections"),
    modules: z.record(z.object({ name: z.string().trim().min(1).max(80), base: z.string().regex(internalPath).min(2).max(200), enabled: z.boolean().optional(), addToMenu: z.boolean().optional(), latestOnHome: z.boolean().optional() }).strict()).optional(),
    hiddenNodes: z.array(z.object({ file: z.string().min(1).max(256), nodeId: z.string().regex(/^[a-z][a-z0-9_-]{0,63}(?:~[a-z][a-z0-9_-]{0,63})?$/i, "Invalid node identifier") }).strict()).max(512).optional(),
    redirects: z.array(z.object({ from: z.string().regex(internalPath), to: z.string().regex(internalPath), prefix: z.literal(true).optional() }).strict()).max(512).optional(),
}).strict();
export function validateContent(input) {
    preflightDocument(input);
    const content = ContentSchema.parse(input);
    for (const [key, record] of Object.entries(content.recordsById))
        if (key !== record.id)
            fail(`Record key ${key} must equal record id`);
    for (const record of Object.values(content.recordsById)) {
        if (record.kind !== "service" && record.kind !== "project" && record.kind !== "item")
            continue;
        if (record.fields.image !== undefined && content.recordsById[record.fields.image]?.kind !== "media")
            fail(`${record.id}.image must be an existing photo`);
    }
    for (const record of Object.values(content.recordsById)) {
        if (record.kind !== "post")
            continue;
        ensureUnique(record.fields.tags, `tag on post ${record.id}`);
        for (const tag of record.fields.tags)
            if (!content.collections[tag]?.group)
                fail(`${record.id}.tags refers to an unknown tag ${tag}`);
    }
    for (const [collectionId, collection] of Object.entries(content.collections)) {
        if (!identifier.safeParse(collectionId).success)
            fail("Invalid collection identifier");
        if (collection.rule) {
            if (collection.rule.kind !== "post")
                fail(`Unknown collection rule kind: ${collection.rule.kind}`);
            if (collection.rule.tag && collection.rule.tag !== collectionId)
                fail(`Collection ${collectionId} rule tag must name itself`);
            if (collection.group && collection.rule.tag !== collectionId)
                fail(`Tag ${collectionId} must select its own posts`);
            if (collection.group?.parentId && !content.collections[collection.group.parentId]?.group)
                fail(`Tag ${collectionId} has an unknown parent`);
            continue;
        }
        ensureUnique(collection.memberIds, `member in collection ${collectionId}`);
        if (collection.designOrder)
            ensureUnique(collection.designOrder, `design-order member in collection ${collectionId}`);
        for (const memberId of collection.memberIds)
            if (!Object.hasOwn(content.recordsById, memberId))
                fail(`Collection ${collectionId} refers to unknown record ${memberId}`);
        for (const memberId of collection.designOrder ?? [])
            if (!collection.memberIds.includes(memberId))
                fail(`Collection ${collectionId} design order refers to unknown member ${memberId}`);
    }
    if (content.hiddenNodes) {
        const sorted = [...content.hiddenNodes].sort((left, right) => left.file.localeCompare(right.file) || left.nodeId.localeCompare(right.nodeId));
        if (JSON.stringify(sorted) !== JSON.stringify(content.hiddenNodes) || new Set(content.hiddenNodes.map((entry) => `${entry.file}\0${entry.nodeId}`)).size !== content.hiddenNodes.length)
            fail("Hidden nodes must be sorted and unique");
    }
    if (content.redirects) {
        ensureUnique(content.redirects.map((entry) => entry.from), "redirect source");
        if (content.redirects.some((entry) => entry.from === entry.to))
            fail("A redirect cannot point to itself");
        const destinations = new Map(content.redirects.map((entry) => [entry.from, entry.to]));
        if (content.redirects.some((entry) => destinations.has(entry.to)))
            fail("Redirect chains are not allowed");
    }
    return content;
}
